import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconPencil, IconTrash, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { ConfirmActionModal, ErrorState, LoadingState, RecordDrawer } from "@how/ui";
import { HouseholdForm } from "./HouseholdForm";
import {
  useDeleteHousehold,
  useHousehold,
  usePeopleOptions,
  useRemoveHouseholdMember,
  useSetHouseholdMember,
  useUpdateHousehold,
  type HouseholdFields,
  type HouseholdMember,
} from "./household-queries";

const ROLE_LABEL: Record<HouseholdMember["role"], string> = {
  adult: "Adult",
  child: "Child",
  other: "Other",
};

export interface HouseholdDrawerProps {
  readonly householdId: string | null;
  readonly onClose: () => void;
}

/**
 * One household, opened from the list. The same pattern as the person record: read through the policies, every
 * write behind a permission the database confirms, and every destructive action through the confirmation modal.
 */
export function HouseholdDrawer({ householdId, onClose }: HouseholdDrawerProps) {
  const record = useHousehold(householdId);
  const canManage = usePermission("households.manage", { write: true });
  const updateHousehold = useUpdateHousehold();
  const deleteHousehold = useDeleteHousehold();
  const setMember = useSetHouseholdMember();
  const removeMember = useRemoveHouseholdMember();

  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [removing, setRemoving] = useState<HouseholdMember | null>(null);
  const [search, setSearch] = useState("");
  const [newMember, setNewMember] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<HouseholdMember["role"]>("adult");
  const [newPrimary, setNewPrimary] = useState(false);

  const household = record.data?.household;
  const members = record.data?.members ?? [];
  const options = usePeopleOptions(
    search,
    members.map((m) => m.personId),
  );
  const manage = canManage.data === true;

  const addressLines =
    household === undefined
      ? []
      : [
          household.address_line1,
          household.address_line2,
          [household.locality, household.region, household.postal_code]
            .filter((part) => part !== null && part.length > 0)
            .join(", "),
        ].filter((line): line is string => line !== null && line.length > 0);

  const close = () => {
    setEditing(false);
    setSearch("");
    setNewMember(null);
    onClose();
  };

  return (
    <RecordDrawer
      opened={householdId !== null}
      onClose={close}
      title={household?.name ?? "Household"}
      subtitle={
        household === undefined ? undefined : (
          <Text size="sm" c="dimmed">
            {members.length === 1 ? "1 member" : `${String(members.length)} members`}
          </Text>
        )
      }
    >
      {record.isPending ? (
        <LoadingState rows={4} />
      ) : record.isError ? (
        <ErrorState
          message="Could not load this household."
          onRetry={() => {
            void record.refetch();
          }}
        />
      ) : editing ? (
        <HouseholdForm
          initial={{
            name: record.data.household.name,
            addressLine1: record.data.household.address_line1 ?? "",
            addressLine2: record.data.household.address_line2 ?? "",
            locality: record.data.household.locality ?? "",
            region: record.data.household.region ?? "",
            postalCode: record.data.household.postal_code ?? "",
          }}
          submitLabel="Save household"
          loading={updateHousehold.isPending}
          failed={updateHousehold.isError}
          onCancel={() => {
            setEditing(false);
          }}
          onSubmit={(fields: HouseholdFields) => {
            updateHousehold.mutate(
              { householdId: record.data.household.id, fields },
              {
                onSuccess: () => {
                  setEditing(false);
                },
              },
            );
          }}
        />
      ) : (
        <Stack gap="lg" mt="md">
          <section>
            <Group justify="space-between" align="center">
              <Title order={4}>Address</Title>
              {manage ? (
                <Button
                  variant="subtle"
                  size="xs"
                  aria-label="Edit name and address"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </Group>
            {addressLines.length === 0 ? (
              <Text size="sm" c="dimmed">
                No address recorded.
              </Text>
            ) : (
              <Stack gap={0} mt="xs">
                {addressLines.map((line) => (
                  <Text key={line} size="sm">
                    {line}
                  </Text>
                ))}
              </Stack>
            )}
          </section>

          <section>
            <Title order={4}>Members</Title>
            {members.length === 0 ? (
              <Text size="sm" c="dimmed" mt="xs">
                Nobody in this household yet.
              </Text>
            ) : (
              <Stack gap="xs" mt="xs">
                {members.map((member) => (
                  <Group key={member.membershipId} justify="space-between" wrap="nowrap">
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm">{member.name}</Text>
                      <Text size="xs" c="dimmed">
                        {ROLE_LABEL[member.role]}
                      </Text>
                      {member.isPrimaryContact ? (
                        <Badge size="sm" variant="light" color="teal">
                          Primary contact
                        </Badge>
                      ) : null}
                    </Group>
                    {manage ? (
                      <Tooltip label={`Remove ${member.name} from this household`}>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Remove ${member.name} from this household`}
                          onClick={() => {
                            setRemoving(member);
                          }}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Group>
                ))}
              </Stack>
            )}
          </section>

          {manage && householdId !== null ? (
            <section>
              <Title order={4}>Add a member</Title>
              <Stack gap="xs" mt="xs">
                <Select
                  label="Person"
                  placeholder="Search by name"
                  searchable
                  searchValue={search}
                  onSearchChange={setSearch}
                  data={options.data ?? []}
                  value={newMember}
                  onChange={setNewMember}
                  nothingFoundMessage={options.isPending ? "Searching" : "Nobody matches"}
                  // The server filters; the dropdown must not filter the server's answer a second time.
                  filter={({ options: all }) => all}
                />
                <Group gap="md" align="flex-end">
                  <Select
                    label="Role"
                    data={[
                      { value: "adult", label: "Adult" },
                      { value: "child", label: "Child" },
                      { value: "other", label: "Other" },
                    ]}
                    value={newRole}
                    onChange={(value) => {
                      if (value === "adult" || value === "child" || value === "other")
                        setNewRole(value);
                    }}
                    allowDeselect={false}
                    w={140}
                  />
                  <Checkbox
                    label="Primary contact"
                    checked={newPrimary}
                    onChange={(event) => {
                      setNewPrimary(event.currentTarget.checked);
                    }}
                    mb={8}
                  />
                </Group>
                <Group justify="flex-end">
                  <Button
                    size="sm"
                    disabled={newMember === null}
                    loading={setMember.isPending}
                    onClick={() => {
                      if (newMember === null) return;
                      setMember.mutate(
                        {
                          householdId,
                          personId: newMember,
                          role: newRole,
                          isPrimaryContact: newPrimary,
                        },
                        {
                          onSuccess: () => {
                            setNewMember(null);
                            setSearch("");
                            setNewPrimary(false);
                          },
                        },
                      );
                    }}
                  >
                    Add to household
                  </Button>
                </Group>
                {setMember.isError ? (
                  <Text size="sm" c="red">
                    Could not add this person. Check your access and try again.
                  </Text>
                ) : null}
              </Stack>
            </section>
          ) : null}

          {manage ? (
            <>
              <Divider />
              <Group justify="flex-start">
                <Button
                  variant="outline"
                  color="red"
                  size="sm"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                >
                  Delete household
                </Button>
              </Group>
            </>
          ) : null}
        </Stack>
      )}

      <ConfirmActionModal
        opened={removing !== null}
        onClose={() => {
          setRemoving(null);
        }}
        title="Remove from household"
        message={
          removing === null || household === undefined
            ? ""
            : `Remove ${removing.name} from ${household.name}? They stay in the directory; only the household link goes.`
        }
        confirmLabel="Remove"
        danger
        loading={removeMember.isPending}
        onConfirm={() => {
          if (removing === null || householdId === null) return;
          removeMember.mutate(
            { householdId, membershipId: removing.membershipId },
            {
              onSuccess: () => {
                setRemoving(null);
              },
            },
          );
        }}
      />

      <ConfirmActionModal
        opened={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        title="Delete household"
        message={
          household === undefined
            ? ""
            : `Delete ${household.name}? Its ${String(members.length)} members stay in the directory without a household, and the address goes with it.`
        }
        confirmLabel="Delete household"
        danger
        {...(household === undefined ? {} : { typeToConfirm: household.name })}
        loading={deleteHousehold.isPending}
        onConfirm={() => {
          if (householdId === null) return;
          deleteHousehold.mutate(householdId, {
            onSuccess: () => {
              setConfirmDelete(false);
              close();
            },
          });
        }}
      />
    </RecordDrawer>
  );
}
